<template>
  <div>
    <div id="main-content">
      <template v-if="!apiStatus.paymentDetails.loading && !apiStatus.refData.loading">
        <div class="content-page-banner">
          <div class="banner-content">
            <h1>{{ $route.meta.title }}</h1>
            <div class="underline"></div>
            <!-- info text -->
            <div v-html="refData.paymentDetailsInformationText.informationText"></div>
          </div>
        </div>
      </template>

      <!-- No DPT Alert -->
      <Alert
        v-if="showNoDptAlert"
        alertType="error"
        message="Your first premium has not been paid. You will need to pay your first premium on your policy before you can make any changes to your premium payments."
      />

      <Alert v-if="apiStatus.paymentDetails.getFailed || apiStatus.refData.fail || apiStatus.creditCardForm.fail" alertType="error" />
      <Alert v-if="submitPolicyError" :message="ErrorStrings.SUBMIT_POLICY_API_ERROR" alertType="error" />
      <Alert v-else-if="apiStatus.paymentDetails.postFailed" alertType="error" message="Sorry we weren’t able to save your changes. Please try submitting it again." />

      <section id="multi-step-form">
        <MultiStepFormNavigation
          :currentPage="pageNumber"
          :links="navLinks()"
          @linkClicked="handleMultiStepFormNavigationClick"
          :disabled="apiStatus.paymentDetails.posting || apiStatus.creditCardForm.loading"
        />

        <ApiLoading
          v-if="apiStatus.paymentDetails.loading || apiStatus.refData.loading || apiStatus.paymentDetails.getFailed || apiStatus.refData.fail"
          :apiError="apiStatus.paymentDetails.getFailed || apiStatus.refData.fail"
          isMemberPortal
        />
        <form id="form-contents" @submit.prevent novalidate v-else>
          <keep-alive>
            <component
              :is="getCurrentStep"
              @updateStepValidation="updateInvalid"
              v-bind="currentStepComponentProps"
              :referenceData="refData"
              @editModeTriggered="editModeTriggered"
              @reinitialiseSelectedPaymentForm="apiStatus.creditCardForm.fail = false"
            />
          </keep-alive>

          <!-- Back/Next buttons -->
          <FormField class="no-bottom-margin">
            <section :class="{ 'grey-box': isLastStep }">
              <!-- Copy above submit button on last step -->
              <template v-if="isLastStep && !apiStatus.paymentDetails.posting">
                <span v-if="this.isDataChanged() && !apiStatus.paymentDetails.posting" v-html="refData.reviewAndSubmitReviewInformationText.informationText" />
                <!-- No changes -->
                <span v-else>
                  <p>There are no changes to submit.</p>
                  <p>If you would like to make a change, please go back and edit your details.</p>
                </span>
              </template>
              <!-- Back/Next/Submit buttons -->
              <div class="form-page-nav" v-if="!apiStatus.paymentDetails.posting">
                <section>
                  <button v-if="pageNumber > 1" @click="updatePaymentDetailsPageNum(pageNumber - 1)" class="secondary">Back</button>
                  <button :disabled="isNextButtonDisabled || isCreditCardFormLoadingOrFailed" @click="processNextButton">
                    {{ getNextBtnLabel }}
                  </button>
                </section>
              </div>
              <!-- Submit API loading -->
              <ApiLoading
                v-if="apiStatus.paymentDetails.posting"
                :apiError="apiStatus.paymentDetails.postFailed"
                isMemberPortal
                loadingMessage="We are processing your request. It shouldn't be too long."
              />
            </section>
          </FormField>
        </form>
      </section>
    </div>
  </div>
</template>

<script>
import cloneDeep from 'lodash.clonedeep';
import isEqual from 'lodash.isequal';
import { MEMBER_MENU } from '@/constants/pageTitles';
import { UpdatePaymentDetailsStepNames } from '@/enums';
import { Alert, ApiLoading, FormField, MultiStepFormNavigation } from '@/components';
import { PREMIUM_IMPACT_PAYMENT_DETAILS } from '@/router/routeNames';
import { setNavigationState } from '@/router/navigationState';
import PaymentDetailsServices from '@/services/PaymentDetails';
import { createUpdatedPaymentDetailsObj } from '@/utils';
import { SubmitPolicyError, SubmitPolicyInProcessError, SubmitPolicyTimeoutError, getCurrentDate } from '@general/js/utils';
import { ErrorStrings } from '@general/js/constants';
import CommonServices from '@/services/Common';
import PaymentDetailsMixin from './PaymentDetailsFormSteps/PaymentDetailsMixin';
import { PremiumPayment, ClaimsAccount, ReviewAndSubmit } from './PaymentDetailsFormSteps';

export default {
  name: 'ViewUpdatePaymentDetails',

  components: {
    Alert,
    ApiLoading,
    FormField,
    MultiStepFormNavigation,
    PremiumPayment,
    ReviewAndSubmit,
    ClaimsAccount,
  },

  mixins: [PaymentDetailsMixin],

  data() {
    return {
      apiStatus: {
        paymentDetails: {
          loading: true,
          getFailed: false,
          posting: false,
          postFailed: false,
        },
        refData: {
          loading: true,
          fail: false,
        },
        creditCardForm: {
          loading: false,
          fail: false,
        },
      },
      steps: {},
      editMode: false,
      initialData: {},
      showNoDptAlert: false,
      submitPolicyError: false,
      ErrorStrings,
    };
  },

  props: {
    /**
     * The member (object) to view/update details for
     */
    selectedMember: {
      type: Object,
    },
  },

  watch: {
    '$route.query.step': {
      handler(val) {
        if (val !== undefined) this.updatePaymentDetailsPageNum(Number(val));
      },
    },
  },

  beforeRouteEnter(to, from, next) {
    if (to.name === PREMIUM_IMPACT_PAYMENT_DETAILS) {
      to.meta.title = MEMBER_MENU.PAYMENT_DETAILS.PREMIUM_IMPACT;
    }
    next();
  },

  beforeRouteLeave(to, from, next) {
    if (this.isDataChanged() && !this.confirmLeave() && !this.submitPolicyInProcess) {
      next(false);
    } else {
      next();
    }
  },

  beforeMount() {
    this.updatePaymentDetailsPageNum(1);
    this.setupSteps();
  },

  async mounted() {
    await this.getPaymentDetailsRefData();
    await this.getPaymentDetails();

    window.addEventListener('beforeunload', this.preventLeavePage);
  },

  beforeUnmount() {
    window.removeEventListener('beforeunload', this.preventLeavePage);
  },

  computed: {
    isNextButtonDisabled() {
      // for last step, button is disabled if no data has changed
      if (this.isLastStep) {
        return !this.isDataChanged();
      }
      // for all other steps, check if next step is disabled
      return this.navLinks().filter((obj) => {
        return obj.pages[0] === this.pageNumber + 1;
      })[0]?.disabled;
    },

    currentStepComponentProps() {
      const baseProps = { initialData: this.initialData };
      const componentMap = {
        [this.steps?.PREMIUM_PAYMENT?.pageNumber]: this.steps?.PREMIUM_PAYMENT,
        [this.steps.CLAIMS_ACCOUNT?.pageNumber]: this.steps.CLAIMS_ACCOUNT,
        [this.steps.REVIEW_AND_SUBMIT.pageNumber]: this.steps.REVIEW_AND_SUBMIT,
      };

      let thisComponent;
      try {
        thisComponent = componentMap[this.pageNumber];
      } catch (error) {
        logger.log('ViewUpdatePaymentDetails:: currentStepComponentProps() - Unexpected page number passed-in', error);
      }

      const a = { ...baseProps, ...thisComponent.props, ref: thisComponent.stepName };
      return a;
    },

    getNextBtnLabel() {
      if (this.isLastStep) {
        return 'Submit payment details';
      }
      return 'Next';
    },

    isLastStep() {
      return this.pageNumber === this.navLinks().length;
    },

    getCurrentStep() {
      return Object.values(this.steps).find((step) => {
        return step.pageNumber === this.pageNumber;
      })?.stepName;
    },

    isCreditCardFormLoadingOrFailed() {
      return this.apiStatus.creditCardForm.loading || this.apiStatus.creditCardForm.fail;
    },
  },

  methods: {
    setupSteps() {
      const allSteps = {
        PREMIUM_PAYMENT: {
          stepName: UpdatePaymentDetailsStepNames.step1,
          pageNumber: 1,
          props: {
            selectedMember: this.selectedMember,
          },
          invalid: false,
          display: 'Premium payment',
        },
        CLAIMS_ACCOUNT: {
          stepName: UpdatePaymentDetailsStepNames.step2,
          pageNumber: 2,
          props: {
            selectedMember: this.selectedMember,
          },
          invalid: true,
          display: 'Claims account',
        },
        REVIEW_AND_SUBMIT: {
          stepName: UpdatePaymentDetailsStepNames.step3,
          pageNumber: 3,
          props: {
            selectedMember: this.selectedMember,
          },
          invalid: false,
          display: 'Review and submit',
        },
      };

      const currSteps = {};

      if (this.isPolicyHolderDetails) {
        // Premium payment step is only shown for PH details
        currSteps.PREMIUM_PAYMENT = allSteps.PREMIUM_PAYMENT;
      }

      currSteps.CLAIMS_ACCOUNT = allSteps.CLAIMS_ACCOUNT;
      currSteps.REVIEW_AND_SUBMIT = allSteps.REVIEW_AND_SUBMIT;

      // assign pageNumber to each page dynamically(because number of pages differ)
      Object.values(currSteps).forEach((step, idx) => {
        step.pageNumber = idx + 1;
      });

      this.steps = cloneDeep(currSteps);
    },

    async processNextButton() {
      if (this.isLastStep) {
        // submit data on last step
        this.postPaymentDetails();
        return;
      }

      if (this.getCurrentStep === UpdatePaymentDetailsStepNames.step1 && this.isPremiumAccountOfTypeCreditCard && this.isIpsiCallbackRequired()) {
        try {
          this.apiStatus.creditCardForm.loading = true;
          await this.$refs[UpdatePaymentDetailsStepNames.step1].submitPaymentForm();
        } catch (error) {
          this.apiStatus.creditCardForm.fail = true;
          return;
        } finally {
          this.apiStatus.creditCardForm.loading = false;
        }
      }
      // move to next step
      this.updatePaymentDetailsPageNum(this.pageNumber + 1);
    },

    // This function is required so that credit card token is fetched when the user goes to next step via multi-step form navigation
    handleMultiStepFormNavigationClick(step) {
      // Check if clicked on step 2. claims account and there is step 1. premium payment, then call processNextButton() to submit the form, otherwise call updatePaymentDetailsPageNum()
      if (step === this.steps.CLAIMS_ACCOUNT.pageNumber && this.getCurrentStep === this.steps.PREMIUM_PAYMENT?.stepName) {
        this.processNextButton();
      } else {
        this.updatePaymentDetailsPageNum(step);
      }
    },

    navLinks() {
      const step = this.steps;
      const navsDisabledForApiLoading =
        this.apiStatus.refData.loading || this.apiStatus.refData.fail || this.apiStatus.paymentDetails.loading || this.apiStatus.paymentDetails.getFailed;
      const menuLinks = [
        {
          linkTitle: step.PREMIUM_PAYMENT?.display,
          pages: [step.PREMIUM_PAYMENT?.pageNumber],
          disabled: false,
          hidden: !step?.PREMIUM_PAYMENT, // hidden if accessing details of member other than PH (i.e Premium payment step will not exist in the step object due to setupSteps() function)
        },
        {
          linkTitle: step.CLAIMS_ACCOUNT?.display,
          pages: [step.CLAIMS_ACCOUNT?.pageNumber],
          disabled: this.getIfStepInvalid(step.CLAIMS_ACCOUNT?.pageNumber) || navsDisabledForApiLoading,
          hidden: !step?.CLAIMS_ACCOUNT,
        },
        {
          linkTitle: step.REVIEW_AND_SUBMIT.display,
          pages: [step.REVIEW_AND_SUBMIT.pageNumber],
          disabled: this.getIfStepInvalid(step.REVIEW_AND_SUBMIT.pageNumber) || navsDisabledForApiLoading,
          hidden: false,
        },
      ];

      const finalMenuLinks = menuLinks.filter((link) => {
        return !link.hidden;
      });
      return finalMenuLinks;
    },

    updateInvalid(data, stepName) {
      const step = this.steps;
      let currentStep;
      switch (stepName) {
        case step?.PREMIUM_PAYMENT?.stepName:
          currentStep = step.PREMIUM_PAYMENT;
          break;

        case step.CLAIMS_ACCOUNT.stepName:
          currentStep = step.CLAIMS_ACCOUNT;
          break;

        default:
          break;
      }

      currentStep.invalid = data;
    },

    /**
     * return true if any of previous step from given step is invalid
     */
    getIfStepInvalid(pageNumber) {
      const thisAndPreviousStepsInvalid = [];
      // convert each steps object to array, making sure order is based on the pageNumber
      const eachSteps = Object.values(this.steps).sort((objA, objB) => {
        return objA.pageNumber - objB.pageNumber;
      });

      eachSteps.forEach((obj) => {
        if (obj.pageNumber < pageNumber) {
          thisAndPreviousStepsInvalid.push(obj.invalid);
        }
      });

      return thisAndPreviousStepsInvalid.some(Boolean);
    },

    isDataChanged() {
      if (!(this.apiStatus.paymentDetails.loading || this.apiStatus.paymentDetails.getFailed || this.apiStatus.refData.loading || this.apiStatus.refData.fail)) {
        const initialData = cloneDeep(this.initialData);
        const paymentDetailsData = cloneDeep(this.paymentDetails);

        // need to delete extra properties for correct comparison (`directDebitAgreement` property only exists in the update accounts request payload but not in retrieve accounts response payload)
        delete paymentDetailsData.claimsAccount.directDebitAgreement;
        delete paymentDetailsData.claimsExtraData;

        return !isEqual(initialData, paymentDetailsData);
      }
      return false;
    },

    /**
     * Returns true if IPSI callback is required
     * If using credit card payment type and payment details have changed, IPSI callback is required
     * @returns {boolean} true if IPSI callback is required, false otherwise
     */
    isIpsiCallbackRequired() {
      if (!this.isPremiumAccountOfTypeCreditCard) {
        return false;
      }

      const initialPaymentDetails = cloneDeep(this.initialData?.premiumPaymentAccount);
      const updatedPaymentDetails = cloneDeep(this.paymentDetails?.premiumPaymentAccount);

      return !isEqual(initialPaymentDetails, updatedPaymentDetails);
    },

    editModeTriggered(editMode) {
      if (!this.membershipInfo?.datePaidTo) {
        this.showNoDptAlert = true;
      }
      this.editMode = editMode;
    },

    async getPaymentDetailsRefData() {
      this.apiStatus.refData.loading = true;
      this.apiStatus.refData.fail = false;

      try {
        const response = await Promise.all([PaymentDetailsServices.getPaymentDetailsPageReferenceData(), PaymentDetailsServices.getPremiumImpactPageReferenceData()]);

        this.updatePaymentDetailsRefData({ ...response[0], ...response[1] }); // save ref data on vuex
      } catch (error) {
        logger.error(error);
        this.apiStatus.refData.fail = true;
      } finally {
        this.apiStatus.refData.loading = false;
      }
    },

    async getPaymentDetails() {
      this.apiStatus.paymentDetails.loading = true;
      this.apiStatus.paymentDetails.getFailed = false;

      try {
        const accessingPersonCode = this.selectedMember?.personCode;
        const memberNumber = this.$route?.params?.policyNumber;

        const data = await PaymentDetailsServices.getPaymentDetails(memberNumber, accessingPersonCode);
        this.updatePaymentDetailsInfo(data);
        this.initialData = cloneDeep(this.paymentDetails);
      } catch (error) {
        logger.error(error);
        this.apiStatus.paymentDetails.getFailed = true;
      } finally {
        this.apiStatus.paymentDetails.loading = false;
      }
    },

    async postPaymentDetails() {
      this.apiStatus.paymentDetails.posting = true;
      this.apiStatus.paymentDetails.postFailed = false;
      this.submitPolicyError = false;
      this.updateSubmitPolicyInProcess(false);

      try {
        const personCode = this.selectedMember?.personCode;
        const memberNumber = this.$route?.params?.policyNumber;
        const paymentDetailsObj = createUpdatedPaymentDetailsObj(this.paymentDetails, this.initialData);

        this.convertIdsToString(paymentDetailsObj);

        const updateAccountsResponse = await PaymentDetailsServices.postPaymentDetails(paymentDetailsObj, memberNumber, personCode);
        this.updateSubmitUpdateSuccessful(true);
        this.apiStatus.paymentDetails.posting = false;

        //  flag to indicate whether to show premium impact vs no impact
        let isPremiumImpacted;
        if (
          (updateAccountsResponse?.oldPremiumAmount === null && updateAccountsResponse?.newPremiumAmount === null) ||
          updateAccountsResponse?.oldPremiumAmount === updateAccountsResponse?.newPremiumAmount
        ) {
          isPremiumImpacted = false;
        } else {
          isPremiumImpacted = true;
        }

        const premiumData = {
          isPremiumImpacted,
          paymentStartDate: this.paymentDetails.premiumPaymentMethod?.paymentStartDate,
          ...updateAccountsResponse,
        };

        setNavigationState({ selectedMember: this.selectedMember, premiumData });
        this.$router.push({
          name: PREMIUM_IMPACT_PAYMENT_DETAILS,
        });
      } catch (error) {
        logger.error(error);
        if (error instanceof SubmitPolicyError) {
          this.submitPolicyError = true;
        }
        if (error instanceof SubmitPolicyInProcessError || error instanceof SubmitPolicyTimeoutError) {
          this.updateSubmitPolicyInProcess(true);

          // Raise work item with payload only for timeout error
          if (error instanceof SubmitPolicyTimeoutError) {
            const paymentDetailsObj = createUpdatedPaymentDetailsObj(this.paymentDetails, this.initialData);
            this.convertIdsToString(paymentDetailsObj);

            const payload = {
              ...paymentDetailsObj,
              currentDate: getCurrentDate(),
            };

            const request = {
              functionName: 'update-account-details',
              workItemCategory: 'OTHERS',
              title: `Submission Timeout for ${this.$route?.params?.policyNumber} in Member Portal`,
              payload,
            };

            await CommonServices.raiseWorkItem(this.$route?.params?.policyNumber, request);
          }

          // Redirect to success page
          setNavigationState({ flowName: 'premium payment details update' });
          this.$router.push({ name: PREMIUM_IMPACT_PAYMENT_DETAILS });
        } else {
          this.apiStatus.paymentDetails.postFailed = true;
          this.updateSubmitUpdateSuccessful(false);
        }
      } finally {
        this.apiStatus.paymentDetails.posting = false;
      }
    },

    convertIdsToString(payload) {
      // convert id to string for premiumPaymentAccount
      if (payload.premiumPaymentAccount?.id) {
        payload.premiumPaymentAccount.id = String(payload.premiumPaymentAccount.id); // id is required as string by BE & EAI
      } else if (payload.premiumPaymentAccount) {
        payload.premiumPaymentAccount.id = null;
      }

      // convert id to string for claimsAccount
      if (payload.claimsAccount?.id) {
        payload.claimsAccount.id = String(payload.claimsAccount.id); // id is required as string by BE & EAI
      } else if (payload.claimsAccount) {
        payload.claimsAccount.id = null;
      }
    },

    confirmLeave() {
      if (this.finishedUpdatingDetails || this.apiStatus.paymentDetails.postFailed || !this.editMode || this.submitPolicyInProcess) return true;
      // eslint-disable-next-line no-alert
      return window.confirm('Do you want to leave? Your unsaved changes will be lost.');
    },

    preventLeavePage(e) {
      // only stop refresh or close browser tab if user made some change without save
      if (this.isDataChanged() && !this.isAutoLogout && this.finishedUpdatingDetails === false && !this.submitPolicyInProcess) {
        // Cancel the event
        e.preventDefault(); // If you prevent default behavior in Mozilla Firefox prompt will always be shown
        // Chrome requires returnValue to be set
        e.returnValue = '';
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.top-content {
  margin-bottom: vutter(1);
}

.review-and-submit {
  .api-loading-wrapper {
    min-height: 80px;
    display: contents;
  }
}
</style>
